// Tenant configuration sections of Account Settings.
//
// These are sections of the existing Settings experience, not a second
// admin console: they mount inside AccountSettingsScreen's tab shell and
// reuse its layout, tokens and header save button.
//
// Everything here writes through PATCH /tenant/config, which enforces an
// allowlist server-side (services/tenantSettings.js). The UI mirrors that
// allowlist rather than defining it — the toggleable module list and the
// configurable terms are fetched from /tenant/settings/schema so the two
// can never drift apart.
//
// Commercial limits are deliberately read-only here. Storage quota and the
// per-file cap are SuperAdmin-owned and set from Tenant Management; this
// screen shows a tenant what they are entitled to and what they have used.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { S, Icon, BtnSecondary } from '../regureStyles';
import { fetchTenantConfig } from '../../redux/actions/tenantConfigActions';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const inputStyle = {
  width: '100%', padding: '10px 12px', border: `1px solid ${S.borderLight}`,
  borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
  background: '#fff', color: S.textPrimary,
};

const SectionLabel = ({ children }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8, marginTop: 20 }}>
    {children}
  </div>
);

const Hint = ({ children }) => (
  <div style={{ fontSize: 12, color: S.textMuted, lineHeight: 1.5, marginBottom: 14 }}>{children}</div>
);

// ─── Shared state ─────────────────────────────────────────────

/**
 * Draft state for every tenant-configurable section, plus one save.
 *
 * The draft is seeded from the tenant's OVERRIDES, not from the resolved
 * vocabulary, so an unconfigured term shows as an empty field with the
 * Regure word as its placeholder. That makes "inherit the default" and
 * "happens to match the default" visibly different, and it is what lets a
 * tenant clear a field to revert.
 */
export function useTenantSettings(config) {
  const dispatch = useDispatch();
  const [schema, setSchema] = useState(null);
  const [vocabulary, setVocabulary] = useState({});
  const [features, setFeatures] = useState({});
  const [branding, setBranding] = useState({ companyName: '', primaryColor: '', accentColor: '' });
  // The legal disclosure printed on every generated proposal. Seeded from the
  // tenant config below and guarded by `seeded` on save for the same reason
  // module visibility is: an empty draft is "not loaded yet", not "the tenant
  // wants no disclaimer", and sending it would blank contractual wording.
  const [disclaimerText, setDisclaimerText] = useState('');
  // The RC Pro "Communications" row, one sentence per document locale. Same
  // `seeded` guard: an unloaded draft must never be sent as three blanks.
  const [communicationsText, setCommunicationsText] = useState({ fr: '', en: '', de: '' });
  // Document signatories (label, person, image, order, document types). Same
  // `seeded` guard: an unloaded draft must never be saved as "no signatories".
  const [signatories, setSignatories] = useState([]);
  const [storage, setStorage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  // Whether the drafts have actually been seeded from the tenant config.
  // Without this, saving before the config resolves would send every module
  // as false and switch the whole tenant off — the draft is empty, not
  // "everything disabled". Absence of data must never read as intent.
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/tenant/settings/schema`, { withCredentials: true });
        if (!cancelled) setSchema(data?.data || null);
      } catch { /* the sections render nothing rather than blocking Settings */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/tenant/storage`, { withCredentials: true });
        if (!cancelled) setStorage(data?.data || null);
      } catch { /* storage panel shows "unavailable" */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Seed drafts once the tenant config is in Redux.
  useEffect(() => {
    if (!config) return;
    setVocabulary(config.vocabularyOverrides || {});
    setFeatures(config.features || {});
    setBranding({
      companyName:  config.branding?.companyName  || '',
      primaryColor: config.branding?.primaryColor || '',
      accentColor:  config.branding?.accentColor  || '',
    });
    setDisclaimerText(config.offerDocuments?.disclaimerText || '');
    const comms = config.offerDocuments?.communicationsText || {};
    setCommunicationsText({ fr: comms.fr || '', en: comms.en || '', de: comms.de || '' });
    setSignatories((config.offerDocuments?.signatories || []).map(s => ({ ...s })));
    setSeeded(true);
  }, [config]);

  const setTerm = useCallback((term, form, value) => {
    setVocabulary(prev => {
      const next = { ...prev, [term]: { ...(prev[term] || {}), [form]: value } };
      // An empty term means "inherit"; drop the key entirely so the payload
      // says nothing about it rather than sending a blank string forever.
      if (!next[term].one && !next[term].other) delete next[term];
      return next;
    });
  }, []);

  const toggleModule = useCallback((key, value) => {
    setFeatures(prev => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const payload = { vocabulary, branding };

      // Module visibility is only sent once the drafts have been seeded
      // from the real config. Sending it beforehand would read an empty
      // draft as "hide everything".
      if (seeded) {
        // Only the modules this UI may change. Posting the whole features
        // object would include keys the allowlist rejects, failing the
        // entire save.
        const featurePatch = {};
        for (const mod of schema?.modules || []) {
          featurePatch[mod.key] = features[mod.key] === true;
        }
        if (Object.keys(featurePatch).length) payload.features = featurePatch;

        // Same guard, and it matters more here: this is contractual wording.
        // Sending the unseeded '' would clear the tenant's legal disclosure
        // from every future proposal. Sent verbatim — no trimming, no case
        // folding, no entity encoding — so accented French survives.
        payload.offerDocuments = {
          disclaimerText,
          communicationsText,
          // The image travels by its own upload route; only the text fields go here.
          signatories: signatories.map(({ id, label, name, order, documentTypes }) => ({
            id, label, name, order: order === '' || order == null ? undefined : Number(order), documentTypes,
          })),
        };
      }

      await axios.patch(`${BASE_URL}/tenant/config`, payload, { withCredentials: true });

      // Re-read so the sidebar, nav labels and brand tokens update without a
      // reload — the config fetch also re-applies vocabulary to i18next.
      await dispatch(fetchTenantConfig());
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Could not save settings');
    }
    setSaving(false);
  }, [vocabulary, features, branding, disclaimerText, communicationsText, signatories, schema, seeded, dispatch]);

  return {
    schema, vocabulary, features, branding, storage, disclaimerText, communicationsText, signatories,
    setTerm, toggleModule, setBranding, setDisclaimerText, setCommunicationsText, setSignatories,
    save, saving, error, saved,
  };
}

// ─── Vocabulary ───────────────────────────────────────────────

export function VocabularySection({ schema, vocabulary, setTerm }) {
  const { t } = useTranslation();
  if (!schema) return <Hint>{t('screens.tenantSettings.loading')}</Hint>;

  return (
    <div>
      <SectionLabel>{t('screens.tenantSettings.vocabTitle')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.vocabHelp')}</Hint>

      <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr 1fr', gap: 10, alignItems: 'center' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {t('screens.tenantSettings.vocabObject')}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {t('screens.tenantSettings.vocabSingular')}
        </div>
        <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          {t('screens.tenantSettings.vocabPlural')}
        </div>

        {schema.terms.map(term => {
          const set = vocabulary[term.key] || {};
          return (
            <React.Fragment key={term.key}>
              <div style={{ fontSize: 13, color: S.textPrimary, fontWeight: 500 }}>
                {term.default.other}
              </div>
              <input
                style={inputStyle}
                value={set.one || ''}
                placeholder={term.default.one}
                maxLength={40}
                onChange={e => setTerm(term.key, 'one', e.target.value)}
              />
              <input
                style={inputStyle}
                value={set.other || ''}
                placeholder={term.default.other}
                maxLength={40}
                onChange={e => setTerm(term.key, 'other', e.target.value)}
              />
            </React.Fragment>
          );
        })}
      </div>

      <Hint>
        <span style={{ display: 'inline-block', marginTop: 14 }}>
          {t('screens.tenantSettings.vocabRevertHint')}
        </span>
      </Hint>
    </div>
  );
}

// ─── Module visibility ────────────────────────────────────────

export function ModulesSection({ schema, features, toggleModule }) {
  const { t } = useTranslation();
  if (!schema) return <Hint>{t('screens.tenantSettings.loading')}</Hint>;

  return (
    <div>
      <SectionLabel>{t('screens.tenantSettings.modulesTitle')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.modulesHelp')}</Hint>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {schema.modules.map(mod => {
          const on = features[mod.key] === true;
          return (
            <div
              key={mod.key}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 14px', borderBottom: `1px solid ${S.borderLight}`,
              }}
            >
              <div>
                <div style={{ fontSize: 14, color: S.textPrimary, fontWeight: 500 }}>{mod.label}</div>
                <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
                  {on ? t('screens.tenantSettings.moduleVisible') : t('screens.tenantSettings.moduleHidden')}
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-label={mod.label}
                onClick={() => toggleModule(mod.key, !on)}
                style={{
                  width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                  background: on ? S.burgundy : S.borderLight, position: 'relative',
                  transition: 'background 120ms ease', flexShrink: 0,
                }}
              >
                <span style={{
                  position: 'absolute', top: 3, left: on ? 23 : 3, width: 18, height: 18,
                  borderRadius: '50%', background: '#fff', transition: 'left 120ms ease',
                }} />
              </button>
            </div>
          );
        })}
      </div>

      <Hint>
        <span style={{ display: 'inline-block', marginTop: 14 }}>
          {t('screens.tenantSettings.modulesDataHint')}
        </span>
      </Hint>
    </div>
  );
}

// ─── Branding ─────────────────────────────────────────────────

const LOGO_SLOTS = [
  { slot: 'logo',         labelKey: 'brandingLogo',        hintKey: 'brandingLogoHint' },
  { slot: 'logoCompact',  labelKey: 'brandingLogoCompact', hintKey: 'brandingLogoCompactHint' },
  { slot: 'documentLogo', labelKey: 'brandingLogoDoc',     hintKey: 'brandingLogoDocHint' },
];

export function BrandingSection({ branding, setBranding, config }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState('');
  const [uploadError, setUploadError] = useState('');

  const urls = useMemo(() => ({
    logo:         config?.branding?.logoUrl || '',
    logoCompact:  config?.branding?.logoCompactUrl || '',
    documentLogo: config?.branding?.documentLogoUrl || '',
  }), [config]);

  const upload = async (slot, file) => {
    if (!file) return;
    setBusy(slot); setUploadError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BASE_URL}/tenant/branding/${slot}`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await dispatch(fetchTenantConfig());
    } catch (e) {
      setUploadError(e?.response?.data?.message || 'Upload failed');
    }
    setBusy('');
  };

  const remove = async (slot) => {
    setBusy(slot); setUploadError('');
    try {
      await axios.delete(`${BASE_URL}/tenant/branding/${slot}`, { withCredentials: true });
      await dispatch(fetchTenantConfig());
    } catch (e) {
      setUploadError(e?.response?.data?.message || 'Could not remove logo');
    }
    setBusy('');
  };

  return (
    <div>
      <SectionLabel>{t('screens.tenantSettings.brandingIdentity')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.brandingHelp')}</Hint>

      <label style={{ fontSize: 13, color: S.textSecondary, display: 'block', marginBottom: 6 }}>
        {t('screens.tenantSettings.brandingCompanyName')}
      </label>
      <input
        style={inputStyle}
        value={branding.companyName}
        maxLength={120}
        placeholder={config?.orgName || ''}
        onChange={e => setBranding(b => ({ ...b, companyName: e.target.value }))}
      />

      <SectionLabel>{t('screens.tenantSettings.brandingLogos')}</SectionLabel>
      {LOGO_SLOTS.map(({ slot, labelKey, hintKey }) => (
        <div key={slot} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: `1px solid ${S.borderLight}` }}>
          <div style={{
            width: 88, height: 44, borderRadius: 8, border: `1px solid ${S.borderLight}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#fff', flexShrink: 0, overflow: 'hidden',
          }}>
            {urls[slot]
              ? <img src={urls[slot]} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              : <Icon name="file-text" size={16} color={S.textMuted} />}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, color: S.textPrimary, fontWeight: 500 }}>
              {t(`screens.tenantSettings.${labelKey}`)}
            </div>
            <div style={{ fontSize: 12, color: S.textMuted, marginTop: 2 }}>
              {t(`screens.tenantSettings.${hintKey}`)}
            </div>
          </div>
          <label style={{ cursor: busy === slot ? 'wait' : 'pointer' }}>
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              style={{ display: 'none' }}
              disabled={busy === slot}
              onChange={e => { upload(slot, e.target.files?.[0]); e.target.value = ''; }}
            />
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
              border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13,
              color: S.textSecondary, background: '#fff',
            }}>
              <Icon name="upload" size={13} color={S.textSecondary} />
              {busy === slot ? t('screens.tenantSettings.uploading') : t('screens.tenantSettings.brandingUpload')}
            </span>
          </label>
          {urls[slot] && (
            <BtnSecondary onClick={() => remove(slot)} style={{ padding: '7px 12px' }}>
              {t('screens.tenantSettings.brandingRemove')}
            </BtnSecondary>
          )}
        </div>
      ))}
      {uploadError && (
        <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          {uploadError}
        </div>
      )}

      <SectionLabel>{t('screens.tenantSettings.brandingColours')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.brandingColourHelp')}</Hint>
      <div style={{ display: 'flex', gap: 18 }}>
        {['primaryColor', 'accentColor'].map(field => (
          <div key={field} style={{ flex: 1 }}>
            <label style={{ fontSize: 13, color: S.textSecondary, display: 'block', marginBottom: 6 }}>
              {t(`screens.tenantSettings.${field}`)}
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="color"
                value={branding[field] || '#800020'}
                onChange={e => setBranding(b => ({ ...b, [field]: e.target.value }))}
                style={{ width: 44, height: 38, padding: 2, border: `1px solid ${S.borderLight}`, borderRadius: 8, background: '#fff', cursor: 'pointer' }}
              />
              <input
                style={{ ...inputStyle, fontFamily: 'monospace' }}
                value={branding[field] || ''}
                placeholder="#800020"
                maxLength={7}
                onChange={e => setBranding(b => ({ ...b, [field]: e.target.value }))}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Storage (read-only) ──────────────────────────────────────

const fmtBytes = (n) => {
  if (n == null) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// ─── Document settings ────────────────────────────────────────
//
// One field today: the legal disclosure printed on every generated proposal.
// It lives here rather than in Tenant Management because it is the tenant's
// own sentence about their own arrangements — who insures, who administers,
// who handles claims — and it changes when those arrangements change. The
// rest of `offerDocuments` (parties, CGA editions, master-contract numbers)
// is carrier contract data and stays SuperAdmin-owned; the server rejects it
// on this route by name.

/** Generous, matches DISCLAIMER_TEXT_MAX server-side. Shown as a counter. */
const DISCLAIMER_MAX = 2000;

export function DocumentSettingsSection({ disclaimerText, setDisclaimerText }) {
  const { t } = useTranslation();
  const value = disclaimerText || '';
  const over = value.length > DISCLAIMER_MAX;

  return (
    <div>
      <SectionLabel>
        {t('screens.tenantSettings.documentsTitle', { defaultValue: 'Proposal documents' })}
      </SectionLabel>
      {/* Two distinct facts, deliberately in two places rather than one long
          paragraph: WHEN a change takes effect goes above the field, and
          WHICH product it governs goes below it next to the counter.
          The scope line matters — this drives the PGMA affiliation preamble
          and nothing else, because RC Pro and PGM carry their disclosure
          inside the FAQ section, which is how the carrier's own
          lawyer-reviewed documents are structured. */}
      <Hint>
        {t('screens.tenantSettings.disclaimerHelp', {
          defaultValue: 'Applies to proposals generated after saving. Documents already '
            + 'generated keep the wording they were issued with.',
        })}
      </Hint>

      <label
        htmlFor="tenant-disclaimer"
        style={{ display: 'block', fontSize: 13, fontWeight: 600, color: S.textPrimary, marginBottom: 6 }}
      >
        {t('screens.tenantSettings.disclaimerLabel', {
          defaultValue: 'Legal disclaimer — proposal documents',
        })}
      </label>

      <textarea
        id="tenant-disclaimer"
        value={value}
        // Straight through to state. No trim, no replace, no normalisation:
        // this is lawyer-approved text and its accents, spacing and
        // punctuation are part of it.
        onChange={(e) => setDisclaimerText(e.target.value)}
        rows={6}
        spellCheck={false}
        style={{
          ...inputStyle,
          minHeight: 132,
          resize: 'vertical',
          lineHeight: 1.6,
          fontFamily: 'inherit',
          borderColor: over ? S.danger : S.borderLight,
        }}
        // Left blank, PGMA falls back to the platform wording — it does NOT
        // print an affiliation form with no disclosure on it. The placeholder
        // said "will omit this paragraph", which was true before the fallback
        // existed and is now simply wrong.
        placeholder={t('screens.tenantSettings.disclaimerPlaceholder', {
          defaultValue: 'Not configured — PGMA proposals use the platform wording.',
        })}
      />

      <div style={{
        display: 'flex', justifyContent: 'space-between', gap: 12,
        fontSize: 12, color: over ? S.danger : S.textMuted, marginTop: 6,
      }}>
        <span>
          {over
            ? t('screens.tenantSettings.disclaimerTooLong', {
                max: DISCLAIMER_MAX,
                defaultValue: `Too long — ${DISCLAIMER_MAX} characters maximum.`,
              })
            : t('screens.tenantSettings.disclaimerSavedNote', {
                defaultValue: 'This text is the disclosure paragraph on PGMA proposals. '
                  + 'RC Pro and PGM carry their disclosure in the FAQ section.',
              })}
        </span>
        <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {value.length} / {DISCLAIMER_MAX}
        </span>
      </div>
    </div>
  );
}

/** Matches COMMUNICATIONS_TEXT_MAX server-side — one table cell, not a paragraph. */
const COMMUNICATIONS_MAX = 500;
const COMMUNICATIONS_LOCALES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

// The RC Pro "Communications" row. One field per DOCUMENT locale, which is not
// the UI language: a French-speaking admin still sets the German sentence. The
// locale names are therefore shown in their own language, untranslated.
export function CommunicationsTextSection({ communicationsText, setCommunicationsText }) {
  const { t } = useTranslation();
  const values = communicationsText || {};

  return (
    <div style={{ marginTop: 28 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: S.textPrimary, marginBottom: 6 }}>
        {t('screens.tenantSettings.communicationsLabel')}
      </label>
      <Hint>{t('screens.tenantSettings.communicationsHelp')}</Hint>

      {COMMUNICATIONS_LOCALES.map(({ code, label }) => {
        const value = values[code] || '';
        const over = value.length > COMMUNICATIONS_MAX;
        const id = `tenant-communications-${code}`;
        return (
          <div key={code} style={{ marginTop: 12 }}>
            <label htmlFor={id} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 4 }}>
              {label}
            </label>
            <textarea
              id={id}
              lang={code}
              value={value}
              // Verbatim, like the disclaimer: this is printed on the contract.
              onChange={(e) => {
                const next = e.target.value;
                setCommunicationsText(prev => ({ ...prev, [code]: next }));
              }}
              rows={2}
              spellCheck={false}
              style={{
                ...inputStyle,
                resize: 'vertical',
                lineHeight: 1.5,
                fontFamily: 'inherit',
                borderColor: over ? S.danger : S.borderLight,
              }}
              placeholder={t('screens.tenantSettings.communicationsPlaceholder')}
            />
            <div style={{
              display: 'flex', justifyContent: 'flex-end',
              fontSize: 12, color: over ? S.danger : S.textMuted, marginTop: 4,
            }}>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {over
                  ? t('screens.tenantSettings.disclaimerTooLong', { max: COMMUNICATIONS_MAX })
                  : `${value.length} / ${COMMUNICATIONS_MAX}`}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Document signatories ─────────────────────────────────────
//
// Who signs the tenant's documents, in order: a label ("Pour …" or a company
// name), an optional person, an optional signature image and the document
// types the signatory appears on. A document type with no signatory keeps its
// built-in signature section. The text fields save with the page's Save
// button; the image uploads straight to the tenant's own storage, so it can
// only be added to a signatory that has been saved.

const SIGNATORY_DOC_TYPES = ['offer', 'policy'];
const SIGNATORIES_MAX = 10;
const newSignatoryId = () => `sig-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function SignatoriesSection({ signatories, setSignatories, config }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [busy, setBusy] = useState('');
  const [imageError, setImageError] = useState('');
  const list = signatories || [];
  // An image can only attach to a signatory the server already knows.
  const savedIds = useMemo(
    () => new Set((config?.offerDocuments?.signatories || []).map(s => s.id)),
    [config],
  );
  const imageUrls = useMemo(
    () => Object.fromEntries((config?.offerDocuments?.signatories || []).map(s => [s.id, s.imageUrl || ''])),
    [config],
  );

  const update = (id, patch) => setSignatories(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)));
  const toggleType = (s, type) => {
    const types = new Set(s.documentTypes || []);
    if (types.has(type)) types.delete(type); else types.add(type);
    update(s.id, { documentTypes: SIGNATORY_DOC_TYPES.filter(x => types.has(x)) });
  };
  const add = () => setSignatories(prev => (prev.length >= SIGNATORIES_MAX ? prev : [...prev, {
    id: newSignatoryId(), label: '', name: '',
    order: prev.reduce((m, s) => Math.max(m, Number(s.order) || 0), 0) + 1,
    documentTypes: [...SIGNATORY_DOC_TYPES],
  }]));
  const remove = (id) => setSignatories(prev => prev.filter(s => s.id !== id));

  const uploadImage = async (id, file) => {
    if (!file) return;
    setBusy(id); setImageError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BASE_URL}/tenant/signatories/${encodeURIComponent(id)}/image`, form, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      await dispatch(fetchTenantConfig());
    } catch (e) {
      setImageError(e?.response?.data?.message || t('screens.tenantSettings.signatoryImageFailed'));
    }
    setBusy('');
  };
  const removeImage = async (id) => {
    setBusy(id); setImageError('');
    try {
      await axios.delete(`${BASE_URL}/tenant/signatories/${encodeURIComponent(id)}/image`, { withCredentials: true });
      await dispatch(fetchTenantConfig());
    } catch (e) {
      setImageError(e?.response?.data?.message || t('screens.tenantSettings.signatoryImageFailed'));
    }
    setBusy('');
  };

  const sorted = [...list].sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));

  return (
    <div style={{ marginTop: 28 }}>
      <SectionLabel>{t('screens.tenantSettings.signatoriesTitle')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.signatoriesHelp')}</Hint>

      {sorted.length === 0 && (
        <div style={{ padding: '12px 14px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary, marginBottom: 12 }}>
          {t('screens.tenantSettings.signatoriesEmpty')}
        </div>
      )}

      {sorted.map((s) => {
        const saved = savedIds.has(s.id);
        const url = imageUrls[s.id];
        const labelId = `signatory-label-${s.id}`;
        const nameId = `signatory-name-${s.id}`;
        const orderId = `signatory-order-${s.id}`;
        return (
          <div key={s.id} style={{ border: `1px solid ${S.borderLight}`, borderRadius: 10, padding: 14, marginBottom: 12, background: '#fff' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 80px', gap: 10 }}>
              <div>
                <label htmlFor={labelId} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 4 }}>
                  {t('screens.tenantSettings.signatoryLabel')}
                </label>
                <input id={labelId} style={inputStyle} value={s.label || ''} maxLength={120}
                  placeholder={t('screens.tenantSettings.signatoryLabelPlaceholder')}
                  onChange={e => update(s.id, { label: e.target.value })} />
              </div>
              <div>
                <label htmlFor={nameId} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 4 }}>
                  {t('screens.tenantSettings.signatoryName')}
                </label>
                <input id={nameId} style={inputStyle} value={s.name || ''} maxLength={120}
                  placeholder={t('screens.tenantSettings.optional')}
                  onChange={e => update(s.id, { name: e.target.value })} />
              </div>
              <div>
                <label htmlFor={orderId} style={{ display: 'block', fontSize: 12, fontWeight: 600, color: S.textMuted, marginBottom: 4 }}>
                  {t('screens.tenantSettings.signatoryOrder')}
                </label>
                <input id={orderId} type='number' min={0} max={999} style={inputStyle} value={s.order ?? ''}
                  onChange={e => update(s.id, { order: e.target.value === '' ? '' : Number(e.target.value) })} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted }}>{t('screens.tenantSettings.signatoryAppearsOn')}</span>
              {SIGNATORY_DOC_TYPES.map(type => (
                <label key={type} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.textPrimary, cursor: 'pointer' }}>
                  <input type='checkbox' checked={(s.documentTypes || []).includes(type)} onChange={() => toggleType(s, type)} />
                  {t(`screens.tenantSettings.signatoryDocType_${type}`)}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
              <div style={{
                width: 140, height: 48, borderRadius: 8, border: `1px dashed ${S.borderLight}`, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', overflow: 'hidden',
              }}>
                {url
                  ? <img src={url} alt={t('screens.tenantSettings.signatoryImageAlt', { label: s.label || '' })} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                  : <span style={{ fontSize: 11, color: S.textMuted }}>{t('screens.tenantSettings.signatoryNoImage')}</span>}
              </div>
              {saved ? (
                <>
                  <label style={{ cursor: busy === s.id ? 'wait' : 'pointer' }}>
                    <input type='file' accept='image/png,image/jpeg' style={{ display: 'none' }} disabled={busy === s.id}
                      onChange={e => { uploadImage(s.id, e.target.files?.[0]); e.target.value = ''; }} />
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px',
                      border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary, background: '#fff',
                    }}>
                      <Icon name='upload' size={13} color={S.textSecondary} />
                      {busy === s.id ? t('screens.tenantSettings.uploading') : t('screens.tenantSettings.signatoryUploadImage')}
                    </span>
                  </label>
                  {url && (
                    <BtnSecondary onClick={() => removeImage(s.id)} style={{ padding: '7px 12px' }}>
                      {t('screens.tenantSettings.brandingRemove')}
                    </BtnSecondary>
                  )}
                </>
              ) : (
                <span style={{ fontSize: 12, color: S.textMuted }}>{t('screens.tenantSettings.signatorySaveFirst')}</span>
              )}
              <div style={{ flex: 1 }} />
              <BtnSecondary onClick={() => remove(s.id)} style={{ padding: '7px 12px' }}>
                {t('screens.tenantSettings.signatoryRemove')}
              </BtnSecondary>
            </div>
          </div>
        );
      })}

      {imageError && (
        <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
          {imageError}
        </div>
      )}

      <BtnSecondary onClick={add} style={{ padding: '7px 12px' }}>
        <Icon name='plus' size={13} /> {t('screens.tenantSettings.signatoryAdd')}
      </BtnSecondary>
    </div>
  );
}

export function StorageSection({ storage }) {
  const { t } = useTranslation();
  if (!storage) return <Hint>{t('screens.tenantSettings.loading')}</Hint>;

  const { limits, usage, percentUsed, level } = storage;
  const barColour = level === 'critical' ? S.danger : level === 'warning' ? S.warning : S.burgundy;

  return (
    <div>
      <SectionLabel>{t('screens.tenantSettings.storageTitle')}</SectionLabel>
      <Hint>{t('screens.tenantSettings.storageManagedHint')}</Hint>

      {usage.available ? (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: S.textPrimary, marginBottom: 6 }}>
            <span>{t('screens.tenantSettings.storageUsed', { used: fmtBytes(usage.bytes) })}</span>
            <span style={{ color: S.textMuted }}>
              {limits.maxStorageGB != null
                ? t('screens.tenantSettings.storageOf', { total: `${limits.maxStorageGB} GB` })
                : t('screens.tenantSettings.storageNoQuota')}
            </span>
          </div>
          {percentUsed != null && (
            <div style={{ height: 8, borderRadius: 4, background: S.borderLight, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, percentUsed)}%`, height: '100%', background: barColour, transition: 'width 200ms ease' }} />
            </div>
          )}
          {level === 'warning' && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
              {t('screens.tenantSettings.storageWarn', { percent: limits.warnAtPercent })}
            </div>
          )}
          {level === 'critical' && (
            <div style={{ marginTop: 10, padding: '8px 12px', background: '#fef2f2', borderRadius: 8, fontSize: 12, color: '#991b1b' }}>
              {t('screens.tenantSettings.storageCritical', { percent: limits.criticalAtPercent })}
            </div>
          )}
        </>
      ) : (
        // Deliberately not a number. The legacy usedSpace counter drifts and
        // showing it against a commercial quota would be worse than silence.
        <div style={{ padding: '10px 14px', background: S.bgCard, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, color: S.textSecondary }}>
          {t('screens.tenantSettings.storageUnavailable')}
        </div>
      )}

      <div style={{ marginTop: 18, display: 'flex', gap: 28 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {t('screens.tenantSettings.storageQuota')}
          </div>
          <div style={{ fontSize: 15, color: S.textPrimary, marginTop: 4 }}>
            {limits.maxStorageGB != null ? `${limits.maxStorageGB} GB` : t('screens.tenantSettings.storageUnlimited')}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {t('screens.tenantSettings.storageFileLimit')}
          </div>
          <div style={{ fontSize: 15, color: S.textPrimary, marginTop: 4 }}>
            {limits.maxFileSizeMB != null ? `${limits.maxFileSizeMB} MB` : t('screens.tenantSettings.storageUnlimited')}
          </div>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            {t('screens.tenantSettings.storageThresholds')}
          </div>
          <div style={{ fontSize: 15, color: S.textPrimary, marginTop: 4 }}>
            {limits.warnAtPercent}% / {limits.criticalAtPercent}%
          </div>
        </div>
      </div>
    </div>
  );
}
